import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { Sparkles, User, FileText } from "lucide-react";

// Each card pair defaults to the existing value — switching to the CV value is always explicit.
const cardSx = (selected) => ({
  flex: 1,
  minWidth: 0,
  p: 1.25,
  borderRadius: "8px",
  cursor: "pointer",
  border: `1px solid ${selected ? "hsl(150 38% 17%)" : "hsl(140 8% 88%)"}`,
  backgroundColor: selected ? "hsl(150 38% 97%)" : "hsl(140 10% 97%)",
  boxShadow: selected ? "0 0 0 1px hsl(150 38% 17%)" : "none",
  opacity: selected ? 1 : 0.7,
  transition: "opacity 120ms, border-color 120ms",
});

const ConflictCard = ({ conflict, choice, onChoose }) => {
  const pct = conflict.confidence != null ? Math.round(conflict.confidence * 100) : null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", mb: 0.5 }}>
        {conflict.kind === "experience" ? `${conflict.label} at ${conflict.company}` : conflict.label}
      </Typography>
      {conflict.kind === "experience" && conflict.dates && (
        <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)", mb: 0.5 }}>{conflict.dates}</Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Box
          role="radio"
          aria-checked={choice === "existing"}
          tabIndex={0}
          onClick={() => onChoose("existing")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChoose("existing")}
          sx={cardSx(choice === "existing")}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <User size={11} color="hsl(150 10% 46%)" />
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)" }}>
              Current profile
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", wordBreak: "break-word" }}>
            {conflict.current || "None"}
          </Typography>
        </Box>
        <Box
          role="radio"
          aria-checked={choice === "cv"}
          tabIndex={0}
          onClick={() => onChoose("cv")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChoose("cv")}
          sx={cardSx(choice === "cv")}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <FileText size={11} color="hsl(150 10% 46%)" />
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)" }}>
              From new CV
            </Typography>
            {pct != null && (
              <Stack direction="row" spacing={0.25} alignItems="center" sx={{ ml: "auto" }}>
                <Sparkles size={9} color="hsl(150 10% 46%)" />
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "hsl(150 10% 46%)" }}>{pct}%</Typography>
              </Stack>
            )}
          </Stack>
          <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", wordBreak: "break-word" }}>
            {conflict.proposed || "None"}
          </Typography>
          {conflict.snippet && (
            <Typography sx={{ fontSize: 11, mt: 0.5, color: "hsl(150 10% 46%)", fontStyle: "italic" }}>
              “{conflict.snippet}”
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

const CvUpdateReviewModal = ({ open, scalarConflicts = [], experienceConflicts = [], additionsCount = 0, onApply }) => {
  const conflicts = [...scalarConflicts, ...experienceConflicts];
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (!open) return;
    setSelections(Object.fromEntries(conflicts.map((c) => [c.id, "existing"])));
    // Keyed on the two conflict lists, not `conflicts` itself — that array is rebuilt every render.
  }, [open, scalarConflicts, experienceConflicts]);

  if (!open) return null;

  const resolvedList = () => conflicts.map((c) => ({ ...c, choice: selections[c.id] || "existing" }));
  const changedCount = Object.values(selections).filter((v) => v === "cv").length;

  return (
    <Dialog
      open={open}
      onClose={() => onApply(conflicts.map((c) => ({ ...c, choice: "existing" })))}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogContent sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 15, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
          Review CV update
        </Typography>
        <Typography sx={{ fontSize: 12, mt: 0.25, mb: 2, color: "hsl(150 10% 46%)" }}>
          {additionsCount > 0 && `${additionsCount} new field${additionsCount === 1 ? "" : "s"} already added. `}
          {conflicts.length} value{conflicts.length === 1 ? "" : "s"} on the CV disagree{conflicts.length === 1 ? "s" : ""} with what is already here. Pick which one to keep for each.
        </Typography>

        {conflicts.map((c) => (
          <ConflictCard
            key={c.id}
            conflict={c}
            choice={selections[c.id] || "existing"}
            onChoose={(choice) => setSelections((prev) => ({ ...prev, [c.id]: choice }))}
          />
        ))}

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: "center" }}>
          <Button
            disableRipple
            onClick={() => setSelections(Object.fromEntries(conflicts.map((c) => [c.id, "cv"])))}
            sx={{
              textTransform: "none", fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 10% 46%)", borderRadius: "8px", height: "auto", px: 2,
              "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
            }}
          >
            Use all CV values
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            disableRipple
            onClick={() => onApply(conflicts.map((c) => ({ ...c, choice: "existing" })))}
            sx={{
              textTransform: "none", fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 10% 46%)", borderRadius: "8px", height: "auto", px: 2,
              "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
            }}
          >
            Keep existing
          </Button>
          <Button
            disableRipple
            disableElevation
            variant="contained"
            onClick={() => onApply(resolvedList())}
            sx={{
              textTransform: "none", minHeight: 0, height: "auto", fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "8px", px: 2,
              backgroundColor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)",
              "&:hover": { backgroundColor: "hsl(150 38% 14%)" },
            }}
          >
            Apply {changedCount > 0 ? `(${changedCount} change${changedCount === 1 ? "" : "s"})` : ""}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CvUpdateReviewModal;
